<!-- Dropdown trigger for switching the site's current language. -->

<script lang="ts" setup>
import { useLanguage } from '@nzyme/vue-i18n/useLanguage.js';
import { useTranslate } from '@nzyme/vue-i18n/useTranslate.js';
import { useService } from '@nzyme/vue-ioc/useService.js';
import { classProp } from '@nzyme/vue-utils/classProp.js';
import { GlobeIcon } from 'lucide-vue-next';
import { useRouter } from 'vue-router';

import { useLocalizedRoute } from '@sigma/client/routes/useLocalizedRoute.js';
import { CountryContext } from '@sigma/common/i18n/CountryContext.js';
import type { Language } from '@sigma/common/i18n/Language.js';
import { SupportedLanguages } from '@sigma/common/i18n/Language.js';
import { formatLocale } from '@sigma/common/i18n/Locale.js';

import Dropdown from './dropdown/Dropdown.vue';
import DropdownExternalLink from './dropdown/DropdownExternalLink.vue';
import * as l from './LanguagePicker.loc.js';

const props = defineProps({
  class: classProp,
});

const t = useTranslate();
const currentLang = useLanguage();
const localizedRoute = useLocalizedRoute();
const router = useRouter();
const countryCtx = useService(CountryContext);

function getLanguageUrl(lang: Language): string {
  const locale = formatLocale(countryCtx(), lang);
  const route = localizedRoute(locale);
  const resolved = router.resolve(route);
  return resolved.href;
}
</script>

<template>
  <Dropdown>
    <template #trigger>
      <button
        :class="[css.trigger, props.class]"
        :aria-label="t(l.selectLanguage)"
      >
        <GlobeIcon :class="css.icon" />
        {{ currentLang }}
      </button>
    </template>

    <DropdownExternalLink
      v-for="lang in SupportedLanguages"
      :key="lang"
      :href="getLanguageUrl(lang)"
      :icon="GlobeIcon"
    >
      {{ lang.toUpperCase() }}
    </DropdownExternalLink>
  </Dropdown>
</template>

<style lang="scss" module="css">
.trigger {
  display: inline-flex;
  align-items: center;
  gap: $spacing-04;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: inherit;
  line-height: 1;
  transition: opacity $transition-duration $transition-ease-out-quad;
  text-transform: uppercase;

  &:hover {
    opacity: 0.7;
  }

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
    border-radius: $border-radius-04;
  }
}

.icon {
  width: 1.2em;
  height: 1.2em;
}
</style>
